import type { MagazineIssue } from '@/data/mock/magazine'

export function magazineSectionId(heading: string) {
  return heading.toLowerCase().replace(/[^a-z0-9]+/g, '-')
}

/** YYYY-MM from publishedAt / publishMonth. */
export function getMagazineMonthKey(date: string | null | undefined): string {
  if (!date) return ''
  const match = date.match(/^(\d{4})-(\d{2})/)
  return match ? `${match[1]}-${match[2]}` : ''
}

/** Display month + year only (e.g. "June 2026"). */
export function formatMagazineMonth(
  date: string | null | undefined,
  style: 'long' | 'short' = 'long',
) {
  const key = getMagazineMonthKey(date)
  if (!key) return ''
  const [year, month] = key.split('-').map(Number)
  return new Date(year, month - 1, 1).toLocaleDateString('en-IN', {
    month: style === 'long' ? 'long' : 'short',
    year: 'numeric',
  })
}

/** @deprecated Use formatMagazineMonth — editions are monthly only. */
export function formatMagazineDate(date: string, style: 'long' | 'month' = 'month') {
  return formatMagazineMonth(date, style === 'long' ? 'long' : 'short')
}

export function padSectionNumber(index: number) {
  return String(index + 1).padStart(2, '0')
}

export interface MagazineMonthOption {
  key: string
  label: string
  count: number
}

export function listMagazineMonths(issues: MagazineIssue[]): MagazineMonthOption[] {
  const counts = new Map<string, number>()
  for (const issue of issues) {
    const key = getMagazineMonthKey(issue.publishedAt)
    if (!key) continue
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }

  return [...counts.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, count]) => ({
      key,
      label: formatMagazineMonth(key, 'long'),
      count,
    }))
}

export function filterMagazinesByMonth(issues: MagazineIssue[], monthKey: string): MagazineIssue[] {
  if (!monthKey || monthKey === 'all') return issues
  return issues.filter((issue) => getMagazineMonthKey(issue.publishedAt) === monthKey)
}

export function getAdjacentMagazineEditions(issues: MagazineIssue[], slug: string) {
  const index = issues.findIndex((issue) => issue.slug === slug)
  if (index < 0) return {}
  return {
    prev: index > 0 ? issues[index - 1] : undefined,
    next: index < issues.length - 1 ? issues[index + 1] : undefined,
  }
}

export function getMagazineEditionForMonth(
  issues: MagazineIssue[],
  monthKey: string,
): MagazineIssue | undefined {
  return issues.find((issue) => getMagazineMonthKey(issue.publishedAt) === monthKey)
}
